import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calculator, Layers, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BatchFormulaDialog({ open, onClose, onApplied }) {
  const [formulas, setFormulas] = useState([]);
  const [models, setModels] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [selectedFormula, setSelectedFormula] = useState('');
  const [selectedModel, setSelectedModel] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const [previewCount, setPreviewCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) {
      fetchFormulas();
      fetchBikeFilters();
      setSelectedFormula('');
      setSelectedModel('all');
      setSelectedSize('all');
      setPreviewCount(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchPreview();
    }
  }, [selectedModel, selectedSize, open]);

  const fetchFormulas = async () => {
    try {
      const res = await axios.get(`${API}/config/price-formulas`);
      setFormulas(res.data);
    } catch { /* ignore */ }
  };

  const fetchBikeFilters = async () => {
    try {
      const res = await axios.get(`${API}/bicycles`);
      const active = res.data.filter(b => b.status !== 'vendida');
      const uniqueModels = [...new Set(active.map(b => b.model))].sort();
      const uniqueSizes = [...new Set(active.map(b => b.size))].sort();
      setModels(uniqueModels);
      setSizes(uniqueSizes);
    } catch { /* ignore */ }
  };

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bicycles/batch-formula/preview`, {
        model: selectedModel,
        size: selectedSize,
      });
      setPreviewCount(res.data.count);
    } catch {
      setPreviewCount(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedFormula) {
      toast.error('Selecciona una fórmula');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/bicycles/batch-formula`, {
        formula_id: selectedFormula,
        model: selectedModel,
        size: selectedSize,
        clear_manual_price: true,
      });
      toast.success(res.data.message);
      onApplied?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar fórmula en lote');
    } finally {
      setApplying(false);
    }
  };

  const selectedFormulaName = formulas.find(f => f.id === selectedFormula)?.name;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="batch-formula-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
            <Layers className="text-orange-500" />
            Aplicar Fórmula en Lote
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Formula selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Calculator size={14} /> Fórmula de Precio
            </Label>
            <Select value={selectedFormula} onValueChange={setSelectedFormula}>
              <SelectTrigger data-testid="batch-formula-select">
                <SelectValue placeholder="Seleccionar fórmula..." />
              </SelectTrigger>
              <SelectContent>
                {formulas.map(f => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name} {f.formula_type === 'percentage' ? `(${f.percentage}%)` : '(Fijo)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formulas.length === 0 && (
              <p className="text-xs text-slate-400">No hay fórmulas. Crea una primero.</p>
            )}
          </div>

          {/* Filters */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Filter size={14} /> Filtrar bicicletas
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger data-testid="batch-model-filter">
                  <SelectValue placeholder="Modelo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los modelos</SelectItem>
                  {models.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedSize} onValueChange={setSelectedSize}>
                <SelectTrigger data-testid="batch-size-filter">
                  <SelectValue placeholder="Talla" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las tallas</SelectItem>
                  {sizes.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-4 text-center" data-testid="batch-preview">
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="spinner w-4 h-4" />
                <span className="text-sm text-slate-500">Calculando...</span>
              </div>
            ) : previewCount !== null ? (
              <div>
                <p className="text-sm text-slate-500">Bicicletas afectadas</p>
                <p className="text-4xl font-bold text-slate-800 font-['Barlow_Condensed']">{previewCount}</p>
                {selectedFormulaName && (
                  <Badge className="mt-2 bg-blue-100 text-blue-700 border-blue-300">
                    {selectedFormulaName}
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Selecciona filtros para ver la vista previa</p>
            )}
          </div>

          <p className="text-xs text-slate-400">
            Se sobrescribirá cualquier precio manual existente en las bicicletas seleccionadas.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleApply}
            disabled={applying || !selectedFormula || previewCount === 0}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="batch-apply-btn"
          >
            {applying ? (
              <><div className="spinner w-4 h-4 mr-2" /> Aplicando...</>
            ) : (
              <>Aplicar a {previewCount ?? '...'} bicicletas</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
